import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { api } from '@/api/client'
import { EntityCrud } from '@/components/entity-crud'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { formatRelative } from '@/lib/format'

import { currenciesKey, fetchCurrencies, type Currency } from './api'

const schema = z.object({
  iso_code: z
    .string()
    .trim()
    .regex(/^[A-Za-z]{3}$/, 'Three letters, e.g. USD'),
  name: z.string().trim().min(1, 'Give it a name').max(64),
  symbol: z.string().max(8),
  rate_to_base: z.string(),
})
type Values = z.infer<typeof schema>

export function CurrenciesScreen({ baseCurrency }: { baseCurrency: string }) {
  const query = useQuery({ queryKey: currenciesKey, queryFn: fetchCurrencies })
  const missingRates = (query.data ?? []).filter(
    (currency) => currency.iso_code !== baseCurrency && currency.rate_updated_at === null,
  )

  return (
    <div className="flex flex-col gap-4">
      {missingRates.length > 0 && (
        <Alert variant="warning">
          <AlertTitle>Exchange rates missing</AlertTitle>
          <AlertDescription>
            {missingRates.map((currency) => currency.iso_code).join(', ')} ha
            {missingRates.length === 1 ? 's' : 've'} never had a rate set, so prices quoted in{' '}
            {missingRates.length === 1 ? 'it' : 'them'} are counted as if 1 unit were 1{' '}
            {baseCurrency}.
          </AlertDescription>
        </Alert>
      )}

      <EntityCrud<Currency, Values>
        entity="currencies"
        singular="currency"
        rows={query.data}
        isPending={query.isPending}
        error={query.error}
        queryKey={currenciesKey}
        keyOf={(row) => row.iso_code}
        labelOf={(row) => row.iso_code}
        protectedRow={(row) =>
          row.iso_code === baseCurrency
            ? 'This is the base currency — every price is converted through it.'
            : null
        }
        columns={[
          {
            header: 'Code',
            cell: (row) => (
              <span className="flex items-center gap-2 font-medium">
                {row.iso_code}
                {row.iso_code === baseCurrency && (
                  <span className="bg-accent text-accent-foreground px-2 py-0.5 text-[0.6875rem]">
                    base
                  </span>
                )}
              </span>
            ),
          },
          { header: 'Name', cell: (row) => row.name },
          { header: 'Symbol', cell: (row) => row.symbol || '—' },
          {
            header: `Rate to ${baseCurrency}`,
            className: 'tabular',
            cell: (row) => Number(row.rate_to_base).toString(),
          },
          {
            header: 'Rate set',
            cell: (row) =>
              row.iso_code === baseCurrency ? (
                <span className="text-muted-foreground">fixed at 1</span>
              ) : row.rate_updated_at ? (
                formatRelative(row.rate_updated_at)
              ) : (
                <span className="text-warning">never</span>
              ),
          },
        ]}
        fields={[
          {
            name: 'iso_code',
            label: 'ISO code',
            createOnly: true,
            placeholder: 'USD',
            description: 'The code identifies the currency and cannot be changed later.',
          },
          { name: 'name', label: 'Name', placeholder: 'US dollar' },
          { name: 'symbol', label: 'Symbol', placeholder: '$' },
          {
            name: 'rate_to_base',
            label: `Rate to ${baseCurrency}`,
            type: 'number',
            placeholder: '0.92',
            description: `One unit of this currency, expressed in ${baseCurrency}.`,
          },
        ]}
        schema={schema}
        emptyValues={{ iso_code: '', name: '', symbol: '', rate_to_base: '' }}
        toValues={(row) => ({
          iso_code: row.iso_code,
          name: row.name,
          symbol: row.symbol,
          rate_to_base: row.rate_updated_at ? String(row.rate_to_base) : '',
        })}
        onCreate={(values) =>
          api.POST('/api/currencies', {
            body: {
              iso_code: values.iso_code.toUpperCase(),
              name: values.name,
              symbol: values.symbol,
              rate_to_base: values.rate_to_base.trim() || null,
            },
          })
        }
        onUpdate={async (row, values) => {
          await api.PUT('/api/currencies/{code}', {
            params: { path: { code: row.iso_code } },
            body: { iso_code: row.iso_code, name: values.name, symbol: values.symbol },
          })
          // The rate has its own endpoint so that editing a name cannot silently
          // reset it; only send it when it actually changed.
          const rate = values.rate_to_base.trim()
          if (rate && row.iso_code !== baseCurrency && Number(rate) !== Number(row.rate_to_base)) {
            await api.PUT('/api/currencies/{code}/rate', {
              params: { path: { code: row.iso_code } },
              body: { rate_to_base: rate },
            })
          }
        }}
        onDelete={(row) =>
          api.DELETE('/api/currencies/{code}', { params: { path: { code: row.iso_code } } })
        }
      />
    </div>
  )
}
